import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import {
  all,
  call,
  debounce,
  put,
  SagaReturnType,
  select,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';
import {QueryFromPage} from '../../../../../../graphql/generated/types';
import {doNotUseThisStartTicketProcess} from '../../../../../buy/ticket/hook/useBuyTicketFlow';
import {LOCAL_STORAGE_KEY_TUTORIAL_EXPOSED} from '../constant/talkViewerConstant';
import {events} from '../constant/talkViewerEvents';
import {TalkViewerState} from '../state/TalkViewerState.interface';
import {
  appendAllExtraTalks,
  changeScene,
  forceProcessToTalkIndex,
  hideFullscreen,
  hideTalkReplayBox,
  pauseAutoTap,
  pauseMedia,
  playAutoTap,
  playMedia,
  replayTalk,
  setAutoTapSpeed,
  setExposedTalk,
  setIsTutorialExposed,
  setMuted,
  showFullscreen,
  showTalkReplayBox,
  stopAutoTap,
  stopMedia,
} from '../state/talkViewerSlice';
import {ViewerEndState} from '../state/ViewerEndState.intreface';
import {ViewerMenuState} from '../state/ViewerMenuState.interface';
import {setIsViewerEndExposed} from '../state/viewerEndSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {AutoTapType} from '../type/AutoTapType.enum';
import {EventType} from '../type/EventType.enum';
import {TalkDataType} from '../type/TalkDataType.enum';
import {TalkMediaStatus} from '../type/TalkMediaStatus.enum';
import {ViewingMode} from '../type/ViewingMode.enum';
import {processNextStep} from './processNextStep.saga';
import {
  selectCanShowViewerEnd,
  selectTalkViewerState,
  selectViewerEndState,
  selectViewerMenuState,
} from './selector';

export function* leaveAndMoveOnToTheNextTrigerredWorker() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const canShowViewerEnd: boolean = yield select(state =>
    talkViewerState.currentViewerInfoData
      ? selectCanShowViewerEnd(state, talkViewerState.currentViewerInfoData)
      : false
  );

  if (!canShowViewerEnd) {
    // 뷰어엔드를 표시할 수 없는 경우. 즉 정주행모드이고 다음 작품이 있을 때에는, 회차 내에서 오토탭을 종료하면서 현재의 오토탭 스피드를 앱에 저장해놓는다.
    // 이후 다음 작품 로딩시 오토탭 스피드를 확인해서, 오토탭 스피드 정보가 있는 경우는 이어서 해당 스피드로 오토탭을 재생해준다.
    if (talkViewerState.internalSettings.autoTap === AutoTapType.Play) {
      window?.KNApp?.setAutoTapSpeed?.(
        talkViewerState.internalSettings.autoSpeed
      );
      yield put(pauseAutoTap());
      return;
    }
    if (window?.KPApp?.onClickNextProduct) {
      window?.KPApp?.onClickNextProduct?.();
    } else {
      const viewerData = talkViewerState.currentProductId
        ? talkViewerState.currentViewerInfoData
        : undefined;
      if (viewerData && viewerData.nextItem) {
        doNotUseThisStartTicketProcess &&
          doNotUseThisStartTicketProcess({
            seriesId: viewerData.seriesItem.seriesId,
            targetProductId: viewerData.nextItem.productId,
            nonstopWatching: true,
            queryFrom: QueryFromPage.Viewer,
            pickExactly: true,
          });
      }
    }
    return;
  }
  yield put(stopAutoTap());
  yield put(setIsViewerEndExposed(true));
}

export function* backgroundTapPressed() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const viewerMenuState: ViewerMenuState = yield select(selectViewerMenuState);
  const {settings, internalSettings} = talkViewerState;
  // Scroll mode에서는 backgroundTap이 동작하지 않는다
  if (settings.viewingMode === ViewingMode.Scroll) {
    return;
  }

  // 오토탭 일시정지 상태 + 정주행모드면 다음편으로 넘어가도 오토탭이 유지되어야 하므로 Pause인 상태면 아무처리하지 않습니다.
  if (
    internalSettings.autoTap !== AutoTapType.Stop &&
    internalSettings.autoTap !== AutoTapType.Pause
  ) {
    yield put(stopAutoTap());
  }

  if (viewerMenuState.isExposed) {
    yield put(setIsViewerMenuExposed({isExposed: false}));
  }

  const processedTalk: SagaReturnType<typeof processNextStep> = yield call(
    processNextStep,
    talkViewerState
  );
  if (processedTalk) {
    const dataType = talkViewerState.talkMap[processedTalk]?.datatype;
    // 탭 진행 중 BGM톡을 만나면 다음톡을 한 번 더 진행한다
    if (dataType === TalkDataType.Bgm) {
      const processedTalkViewerState: TalkViewerState = yield select(
        selectTalkViewerState
      );
      yield call(processNextStep, processedTalkViewerState);
    }
  }
}

export function* talkTapPressed({
  payload,
}: ReturnType<typeof events.talkTapPressed>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  if (talkViewerState.shownReplay) {
    yield put(hideTalkReplayBox());
  }
  const {talkId} = payload;
  const {datatype, media} = talkViewerState.talkMap[talkId];
  const {mediaStatus} = talkViewerState.talkInfoMap[talkId];

  if (talkViewerState.internalSettings.autoTap !== AutoTapType.Stop) {
    yield put(stopAutoTap());
  }

  switch (datatype) {
    case TalkDataType.FullscreenVideo:
    case TalkDataType.Movie: {
      yield put(showFullscreen({isAuto: false, talkId}));
      yield put(playMedia(talkId));
      break;
    }
    case TalkDataType.Sound:
    case TalkDataType.Bgm: {
      if (mediaStatus === TalkMediaStatus.Play) {
        yield put(stopMedia(talkId));
      } else {
        yield put(playMedia(talkId));
      }
      break;
    }
    case TalkDataType.TalkEffect: {
      if (media) {
        yield put(playMedia(talkId));
      }
      break;
    }
    // 이모티콘 클릭 처리는 이모티콘 컴포넌트 내에서 한다.
    case TalkDataType.Emoticon: {
      break;
    }
    // 특정한 처리가 필요한 톡 외에는 배경을 탭 한 걸로 간주한다.
    default: {
      yield put(events.backgroundTapPressed());
    }
  }
}

export function* talkLongTapPressed({
  payload,
}: ReturnType<typeof events.talkLongTapPressed>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  if (talkViewerState.settings.viewingMode === ViewingMode.Scroll) {
    return;
  }
  // 톡 롱탭시에 오토탭은 정지함
  yield put(stopAutoTap());

  const {talkId} = payload;
  // 풀스크린 상태에서는 아무 동작하지 않음
  if (talkViewerState.fullscreen?.talkId === talkId) {
    return;
  }

  yield put(showTalkReplayBox(talkId));
}

/**
 * 뷰어 최상단에서 아래로 당기는 동작을 했을 때 발생
 */
export function* ViewerPulledDown() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const viewerEndState: ViewerEndState = yield select(selectViewerEndState);
  const {productInfoMap, currentProductId, sceneMap} = talkViewerState;
  if (!currentProductId) {
    return;
  }
  const product = productInfoMap[currentProductId];
  if (viewerEndState.isExposed) {
    yield put(forceProcessToTalkIndex(product.progress.totalTalkCount));
    yield put(setIsViewerEndExposed(false));
  } else {
    const currentSceneId = product.currentSceneId;
    if (!currentSceneId) {
      return;
    }
    const prevSceneId = sceneMap[currentSceneId].prevSceneId;
    if (prevSceneId) {
      yield put(changeScene(prevSceneId));
      yield put(appendAllExtraTalks());
    }
  }
}

/**
 * 뷰어 최하단에서 상단으로 당기는 동작을 했을 때 발생
 */
export function* ViewerPulledUp() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {productInfoMap, currentProductId, sceneMap} = talkViewerState;
  if (!currentProductId) {
    return;
  }
  const product = productInfoMap[currentProductId];
  const currentSceneId = product.currentSceneId;
  if (!currentSceneId) {
    return;
  }
  const nextSceneId = sceneMap[currentSceneId].nextSceneId;
  if (nextSceneId) {
    yield put(changeScene(nextSceneId));
    // Scroll mode인 경우 모든 남은 톡을 추가해준다.
    if (talkViewerState.settings.viewingMode === ViewingMode.Scroll) {
      yield put(appendAllExtraTalks());
    }
  }
  // 다음 씬이 없는데 pull된 경우 뷰어를 벗어난 걸로 판단한다 (뷰어엔드 노출 등의 동작을 한다)
  else {
    yield put(events.leaveAndMoveOnToTheNextTrigerred());
  }
}

export function* endOfTalkReached() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  if (talkViewerState.fullscreen) {
    yield put(events.fullscreenHideRequested());
  }
  // 미리보기인 경우 톡이 모두 끝났으면 아무 동작하지 않는다.
  if (talkViewerState.isPreview) {
    return;
  }
  // 마지막 톡 이후에 다음 프로세스 이벤트가 발생시에는 뷰어엔드노출/다음편 이동 로직을 실행한다
  yield put(events.leaveAndMoveOnToTheNextTrigerred());
}

export function* tutorialTapPressed() {
  window.localStorage.setItem(LOCAL_STORAGE_KEY_TUTORIAL_EXPOSED, 'Y');
  yield put(setIsTutorialExposed(false));
}

export function* viewerSwipedUp() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const viewerMenuState: ViewerMenuState = yield select(selectViewerMenuState);
  if (talkViewerState.internalSettings.autoTap === AutoTapType.Play) {
    yield put(stopAutoTap());
  }
  if (viewerMenuState.isExposed) {
    yield put(setIsViewerMenuExposed({isExposed: false}));
  }
}

export function* viewerSwipedDown() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const viewerMenuState: ViewerMenuState = yield select(selectViewerMenuState);
  if (talkViewerState.internalSettings.autoTap === AutoTapType.Play) {
    yield put(stopAutoTap());
  }
  if (
    !viewerMenuState.isExposed &&
    // 튜토리얼 노출중이 아닐 때면 뷰어메뉴를 띄워준다.
    !talkViewerState.isTutorialExposed
  ) {
    yield put(setIsViewerMenuExposed({isExposed: true}));
  }
}

export function* viewerPaused() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {internalSettings, sound, bgm, movie, fullscreen} = talkViewerState;
  if (internalSettings.autoTap === AutoTapType.Play) {
    yield put(stopAutoTap());
  }
  if (sound) {
    yield put(stopMedia(sound.talkId));
  }
  if (bgm) {
    yield put(stopMedia(bgm.talkId));
  }
  if (movie) {
    yield put(fullscreen ? pauseMedia(movie.talkId) : stopMedia(movie.talkId));
  }
}

export function* replayBoxTapPressed({
  payload,
}: ReturnType<typeof events.replayBoxTapPressed>) {
  const {talkId} = payload;
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {productInfoMap, currentProductId, sceneInfoMap} = talkViewerState;
  if (!currentProductId) {
    return;
  }
  const currentProduct = productInfoMap[currentProductId];
  const {currentSceneId} = currentProduct;
  if (!currentSceneId) {
    return;
  }
  const currentScene = sceneInfoMap[currentSceneId];
  if (!currentScene) {
    return;
  }
  const {talkHistory} = currentScene;
  const lastTalkId = last(talkHistory);
  // 현재 씬의 마지막톡에 톡 다시보기를 했을 경우는 별다른 처리 없이 다시보기 박스를 닫고 끝낸다.
  if (lastTalkId === talkId) {
    yield put(hideTalkReplayBox());
    return;
  }
  yield put(replayTalk(talkId));
}

export function* productExposedAreaChanged({
  payload,
}: ReturnType<typeof events.productExposedAreaChanged>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {currentProductId, productInfoMap} = talkViewerState;
  if (!currentProductId) {
    return;
  }
  const product = productInfoMap[currentProductId];
  if (!product) {
    return;
  }
  // 기존 노출톡과 현재 노출톡과 동일한 경우는 아무 처리하지 않음
  if (product.exposed && isEqual(product.exposed, payload)) {
    return;
  }
  yield put(setExposedTalk(payload));
}

export function* autoTapChangeStatusTapPressed({
  payload,
}: ReturnType<typeof events.autoTapChangeStatusTapPressed>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  // 스크롤모드에서는 오토탭 관련 동작하지 않음
  if (talkViewerState.settings.viewingMode === ViewingMode.Scroll) {
    return;
  }
  switch (payload.autoTapType) {
    case AutoTapType.Play: {
      yield put(playAutoTap());
      break;
    }
    case AutoTapType.Pause: {
      yield put(pauseAutoTap());
      break;
    }
    case AutoTapType.Stop: {
      yield put(stopAutoTap());
      break;
    }
  }
}

export function* autoTapSpeedChangeTapPressed({
  payload,
}: ReturnType<typeof events.autoTapChangeSpeedTapPressed>) {
  yield put(setAutoTapSpeed(payload.autoTapSpeed));
  yield put(playAutoTap());
  yield put(setIsViewerMenuExposed({isExposed: false}));
}

export function* mutedToggleTapPresssed() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  yield put(setMuted(!talkViewerState.internalSettings.muted));
}

export function* mediaEnded({payload}: ReturnType<typeof events.mediaEnded>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {talkId} = payload;
  yield put(stopMedia(talkId));
  if (talkViewerState.fullscreen) {
    yield put(hideFullscreen());
  }
}

export function* fullscreenHideRequested() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {fullscreen, talkMap} = talkViewerState;
  const fullscreenTalkId = fullscreen?.talkId;
  if (!fullscreenTalkId) {
    return;
  }
  const talkDataType = talkMap[fullscreenTalkId].datatype;
  if (
    talkDataType === TalkDataType.Movie ||
    talkDataType === TalkDataType.FullscreenVideo
  ) {
    yield put(stopMedia(fullscreenTalkId));
  }
  yield put(hideFullscreen());
}

export function* fullscreenImageTapPressed() {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {fullscreen, talkMap, currentProductId, productInfoMap} =
    talkViewerState;
  const fullscreenTalkId = fullscreen?.talkId;
  if (
    !fullscreenTalkId ||
    talkMap[fullscreenTalkId].datatype !== TalkDataType.FullscreenImage ||
    !currentProductId
  ) {
    return;
  }

  const product = productInfoMap[currentProductId];
  // 풀스크린 이미지가 회차 마지막 톡인 경우 풀스크린 이미지만 닫고 끝낸다.
  if (talkMap[fullscreenTalkId].talkIndex === product.progress.totalTalkCount) {
    yield put(events.fullscreenHideRequested());
    return;
  }

  const latestTalkViewerState: TalkViewerState = yield select(
    selectTalkViewerState
  );
  yield call(processNextStep, latestTalkViewerState);
}

export function* viewerMenuExposedToggleTapPressed() {
  const viewerMenuState: ViewerMenuState = yield select(selectViewerMenuState);
  yield put(setIsViewerMenuExposed({isExposed: !viewerMenuState.isExposed}));
}

export function* forceProcessToTalkId({
  payload,
}: ReturnType<typeof events.forceProcessToTalkId>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {talkMap} = talkViewerState;
  const {talkId} = payload;
  const foundTalk = talkMap[talkId];
  if (!foundTalk) {
    console.error(
      '현재 product에 해당 talk id가 존재하지 않음.',
      talkMap,
      talkId
    );
    return;
  }

  yield put(forceProcessToTalkIndex(foundTalk.talkIndex));
}

export function* forceProcessToTalkGroupId({
  payload,
}: ReturnType<typeof events.forceProcessToTalkGroupId>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {talkGroupMap} = talkViewerState;
  const {talkGroupId} = payload;
  const foundTalkGroup = talkGroupMap[talkGroupId];
  if (!foundTalkGroup) {
    console.error(
      '현재 product에 해당 talk group id가 존재하지 않음',
      talkGroupMap,
      talkGroupId
    );
    return;
  }
  const firstTalk = foundTalkGroup.talkIds[0];
  yield put(events.forceProcessToTalkId(firstTalk));
}

export function* forceProcessToSceneId({
  payload,
}: ReturnType<typeof events.forceProcessToSceneId>) {
  const talkViewerState: TalkViewerState = yield select(selectTalkViewerState);
  const {sceneMap} = talkViewerState;
  const {sceneId} = payload;
  const foundScene = sceneMap[sceneId];
  if (!foundScene) {
    console.error(
      '현재 product에 해당 sceneId가 존재하지 않음',
      sceneMap,
      sceneId
    );
    return;
  }
  const firstTalkGroup = foundScene.talkGroupIds[0];
  yield put(events.forceProcessToTalkGroupId(firstTalkGroup));
}

export default function* talkViewerEventSaga() {
  yield all([
    takeEvery(EventType.BackgroundTapPressed, backgroundTapPressed),
    takeLeading(
      EventType.LeaveAndMoveOnToTheNextTrigerred,
      leaveAndMoveOnToTheNextTrigerredWorker
    ),
    takeEvery(EventType.TalkTapPressed, talkTapPressed),
    takeEvery(EventType.TalkLongTapPressed, talkLongTapPressed),
    takeEvery(EventType.ViewerSwipedUp, viewerSwipedUp),
    takeEvery(EventType.ViewerSwipedDown, viewerSwipedDown),
    takeEvery(EventType.ViewerPulledUp, ViewerPulledUp),
    takeEvery(EventType.ViewerPulledDown, ViewerPulledDown),
    takeEvery(EventType.EndOfTalkReached, endOfTalkReached),
    takeEvery(EventType.TutorialTapPressed, tutorialTapPressed),
    takeEvery(EventType.ViewerPaused, viewerPaused),
    takeEvery(EventType.ReplayBoxTapPressed, replayBoxTapPressed),
    debounce(
      50,
      EventType.ProductExposedAreaChanged,
      productExposedAreaChanged
    ),
    takeEvery(
      EventType.AutoTapChangeStatusTapPressed,
      autoTapChangeStatusTapPressed
    ),
    takeEvery(
      EventType.AutoTapChangeSpeedTapPressed,
      autoTapSpeedChangeTapPressed
    ),
    takeEvery(EventType.MutedToggleTapPresssed, mutedToggleTapPresssed),
    takeEvery(EventType.MediaEnded, mediaEnded),
    takeEvery(EventType.FullscreenHideRequested, fullscreenHideRequested),
    takeEvery(
      EventType.ViewerMenuExposedToggleTapPressed,
      viewerMenuExposedToggleTapPressed
    ),
    takeEvery(EventType.FullscreenImageTapPressed, fullscreenImageTapPressed),
    takeEvery(EventType.ForceProcessToTalkId, forceProcessToTalkId),
    takeEvery(EventType.ForceProcessToTalkGroupId, forceProcessToTalkGroupId),
    takeEvery(EventType.ForceProcessToSceneId, forceProcessToSceneId),
  ]);
}
